import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { eur } from "@/lib/api";
import { useMeta } from "@/lib/meta";
import { useCart } from "@/lib/cart";
import { discounted, tierDiscount } from "@/lib/layout";
import { ProductThumb } from "@/components/ProductThumb";
import { SizeText, RichDescription } from "@/components/RichText";

export function AccessoryPage({ product }) {
  const meta = useMeta();
  const cart = useCart();
  const navigate = useNavigate();
  const [ink, setInk] = useState(product.ink_colors[0] || "black");
  const [qty, setQty] = useState(1);
  const tiers = product.config?.qty_tiers || [];
  const unit = discounted(product.price, tiers, qty);
  const pct = tierDiscount(tiers, qty);
  const add = () => {
    cart.add({ product_id: product.id, slug: product.slug, name: product.name, sku: product.sku, kind: "pad", image: product.image, quantity: qty, ink_color: ink,
      unit_price: unit, base_price: product.price, tiers, design: {}, layout: null, pads: 0, pad_price: 0 });
    toast.success("Aggiunto al carrello", { action: { label: "Vai al carrello", onClick: () => navigate("/carrello") } });
  };
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-2 gap-10" data-testid="accessory-page">
      <div className="ink-grid rounded-xl h-80 flex items-center justify-center p-6"><ProductThumb p={product} w={320} h={280} /></div>
      <div className="space-y-5">
        <div>
          <div className="font-mono text-[11px] tracking-widest text-slate-500 uppercase">{product.brand}{product.sku && ` · Cod. ${product.sku}`}</div>
          <h1 className="text-4xl font-extrabold uppercase leading-tight" data-testid="product-name"><SizeText text={product.name} /></h1>
          <RichDescription html={product.description} className="text-sm text-slate-600 mt-2" testid="product-description" />
        </div>
        <div>
          <div className="font-mono text-[10px] tracking-widest text-slate-500 uppercase mb-2">Colore inchiostro</div>
          <div className="flex flex-wrap gap-2">
            {meta?.inks.filter((i) => product.ink_colors.includes(i.id)).map((i) => (
              <button key={i.id} data-testid={`ink-color-${i.id}`} onClick={() => setInk(i.id)}
                className={`press flex items-center gap-1.5 h-8 pl-1 pr-2.5 rounded-full border text-xs ${ink === i.id ? "border-brand-navy ring-2 ring-brand-navy/10" : "border-slate-200"}`}>
                <span className="w-6 h-6 rounded-full" style={{ background: i.hex }} /> {i.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border bg-slate-50 p-4">
          <div className="flex items-center border border-slate-300 rounded-md bg-white h-9">
            <button data-testid="qty-dec-btn" className="w-9 h-full flex items-center justify-center" onClick={() => setQty(Math.max(1, qty - 1))}><Minus className="w-3.5 h-3.5" /></button>
            <span data-testid="qty-value" className="w-8 text-center font-mono text-sm">{qty}</span>
            <button data-testid="qty-inc-btn" className="w-9 h-full flex items-center justify-center" onClick={() => setQty(Math.min(100, qty + 1))}><Plus className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-right">
            <div className="text-3xl font-display font-extrabold" data-testid="editor-total-price">{eur(unit * qty)}</div>
            <div className="text-[11px] text-slate-500">{eur(unit)} cad. IVA incl.{pct > 0 && <b className="text-green-700 ml-1">-{pct}%</b>}</div>
          </div>
        </div>
        <button data-testid="add-to-cart-btn" onClick={add} className="press w-full h-12 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center gap-2 hover:bg-brand-yellowdark">
          <ShoppingBag className="w-4 h-4" /> Aggiungi al carrello
        </button>
      </div>
    </div>
  );
}
